/**
 * Dashboard de métricas do Routing Engine (F1.4).
 *
 * Permissão: P_ROUTING_ENGINE_MANAGE. Mostra agregados de
 * `orchestrator_decisions` e `agent_sessions` em janelas de tempo (24h/7d/30d).
 * Versão MVP — sem gráficos, apenas cards numéricos + lista das últimas
 * decisões (debug rápido).
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart2, AlertCircle, Zap, TimerReset, DollarSign, Activity } from "lucide-react";

const OUTCOME_LABELS: Record<string, string> = {
  converted: "Convertidas",
  resolved: "Resolvidas",
  escalated: "Escaladas",
  abandoned: "Abandonadas",
  pending: "Em curso",
};

const OUTCOME_COLORS: Record<string, string> = {
  converted: "text-green-600",
  resolved: "text-blue-600",
  escalated: "text-amber-600",
  abandoned: "text-muted-foreground",
  pending: "text-muted-foreground",
};

function formatNumber(n: number): string {
  return n.toLocaleString("pt-BR");
}
function formatMoney(n: number): string {
  return `US$ ${n.toFixed(4)}`;
}
function formatMs(n: number | null): string {
  if (n === null) return "—";
  return `${n}ms`;
}

export default function RoutingMetrics() {
  const [window, setWindow] = useState<"24h" | "7d" | "30d">("24h");

  const { data: snapshot, isLoading } = trpc.routingMetrics.snapshot.useQuery({ window });
  const { data: recent = [] } = trpc.routingMetrics.recentDecisions.useQuery({ limit: 30 });

  return (
    <PermissionGuard permission="ai:routing_engine_manage">
      <DashboardLayout>
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <BarChart2 className="w-6 h-6 text-indigo-500" />
                Métricas do Routing Engine
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Decisões, custo, latência e outcomes dos agentes IA.
              </p>
            </div>
            <Select value={window} onValueChange={(v) => setWindow(v as typeof window)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Últimas 24h</SelectItem>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading || !snapshot ? (
            <div className="text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <>
              {/* Cards principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-border/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-7 h-7 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {formatNumber(snapshot.totalDecisions)}
                        </p>
                        <p className="text-xs text-muted-foreground">Decisões</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-7 h-7 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {formatNumber(snapshot.errorDecisions)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Erros / falhas
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-7 h-7 text-amber-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {formatNumber(snapshot.handoffCount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Handoffs ({formatNumber(snapshot.loopCount)} por loop)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-7 h-7 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {formatMoney(snapshot.estimatedCostUsd)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Custo IA (estimado)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cards secundários */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="border-border/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <TimerReset className="w-7 h-7 text-violet-500" />
                      <div>
                        <p className="text-lg font-bold">
                          {formatMs(snapshot.latencyMsP50)} <span className="text-sm text-muted-foreground">/ {formatMs(snapshot.latencyMsP95)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Latência p50 / p95
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50 md:col-span-2">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-muted-foreground mb-2">
                      Outcomes das sessões (atualizado pelo job diário)
                    </p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {Object.entries(snapshot.byOutcome).length === 0 ? (
                        <span className="text-muted-foreground">Sem dados</span>
                      ) : (
                        Object.entries(snapshot.byOutcome).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-1.5">
                            <span className={OUTCOME_COLORS[k] ?? "text-muted-foreground"}>
                              {OUTCOME_LABELS[k] ?? k}:
                            </span>
                            <span className="font-medium">{formatNumber(v)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Decisões recentes */}
              <div>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">
                  Decisões recentes
                </h2>
                {recent.length === 0 ? (
                  <Card className="border-dashed border-2 border-border/50">
                    <CardContent className="py-8 text-center text-sm text-muted-foreground">
                      Nenhuma decisão registrada ainda. Ative o Routing Engine em um
                      fluxo para começar.
                    </CardContent>
                  </Card>
                ) : (
                  <div className="flex flex-col gap-2">
                    {recent.map((d) => (
                      <Card key={d.id} className="border-border/50">
                        <CardContent className="pt-3 pb-3">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <Badge variant="outline" className="text-xs">
                                conv #{d.conversationId}
                              </Badge>
                              {d.error ? (
                                <Badge variant="outline" className="text-xs text-red-600">
                                  erro
                                </Badge>
                              ) : null}
                              <span className="text-xs text-muted-foreground truncate">
                                {(d.toolsCalled ?? [])
                                  .map((t: { name: string }) => t.name)
                                  .join(" · ") || "—"}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{formatMs(d.latencyMs)}</span>
                              <span>{formatNumber(d.tokensUsed ?? 0)} toks</span>
                              <span>{new Date(d.decidedAt).toLocaleString("pt-BR")}</span>
                            </div>
                          </div>
                          {d.error && (
                            <p className="text-xs text-red-600 mt-1 truncate">
                              {d.error}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DashboardLayout>
    </PermissionGuard>
  );
}
